import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, X, Check, Image, Trash2, Tag, FolderOpen, Grid, List, Search,
  ChevronLeft, ChevronRight, Loader, AlertCircle, CheckSquare, Square,
  Download, Layers, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'all', label: 'All Assets' },
  { id: 'uncategorized', label: 'Uncategorized' },
  { id: 'suds-screen', label: 'SUDS Screens' },
  { id: 'suds-splash', label: 'SUDS Splash' },
  { id: 'smithers-xi', label: 'Smithers XI' },
  { id: 'velvet-note', label: 'Velvet Note' },
  { id: 'creators-agent', label: "Creator's Agent" },
  { id: 'creators-stake', label: "Creator's Stake" },
  { id: 'quadra-core', label: 'QUADRA Core' },
  { id: 'logo-emblem', label: 'Logos & Emblems' },
  { id: 'icon-graphic', label: 'Icons & Graphics' },
  { id: 'social-ad', label: 'Social Ads' },
  { id: 'popup-ad', label: 'Pop-Up Ad' },
  { id: 'cta', label: 'CTA' },
  { id: 'product-photo', label: 'Product Photos' },
  { id: 'hero-banner', label: 'Hero Banners' },
  { id: 'lifestyle', label: 'Lifestyle' },
  { id: 'branding', label: 'Branding' },
  { id: 'merch-mockup', label: 'Merch Mockups' },
  { id: 'avatar', label: 'Avatar Assets' },
  { id: 'future-use', label: 'Future Use' },
];

export default function AssetManagerPage() {
  const [assets, setAssets] = useState([]);
  const [totalAssets, setTotalAssets] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [category, setCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, files: [] });
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef(null);

  // Multi-select state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchAction, setBatchAction] = useState(null); // 'category' | 'export' | null
  const [batchProcessing, setBatchProcessing] = useState(false);

  const fetchAssets = useCallback(async () => {
    try {
      const res = await fetch(`${API}/assets/list?category=${category}&page=${page}&limit=48`);
      const data = await res.json();
      setAssets(data.assets || []);
      setTotalAssets(data.total || 0);
      setPages(data.pages || 1);
    } catch (err) {
      console.error('Failed to fetch assets:', err);
    }
  }, [category, page]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  // Exit select mode clears selection
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setBatchAction(null);
  };

  const toggleSelect = (assetId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(assetId)) next.delete(assetId);
      else next.add(assetId);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === filteredAssets.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAssets.map(a => a.asset_id)));
    }
  };

  // Batch category assignment
  const batchSetCategory = async (newCategory) => {
    if (selectedIds.size === 0) return;
    setBatchProcessing(true);
    try {
      await fetch(`${API}/assets/batch/category`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asset_ids: [...selectedIds], category: newCategory }),
      });
      setAssets(prev => prev.map(a =>
        selectedIds.has(a.asset_id) ? { ...a, category: newCategory } : a
      ));
      setBatchAction(null);
    } catch (err) {
      console.error('Batch category failed:', err);
    }
    setBatchProcessing(false);
  };

  // Batch delete
  const batchDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} assets permanently?`)) return;
    setBatchProcessing(true);
    try {
      await fetch(`${API}/assets/batch/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asset_ids: [...selectedIds] }),
      });
      setAssets(prev => prev.filter(a => !selectedIds.has(a.asset_id)));
      setTotalAssets(prev => prev - selectedIds.size);
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Batch delete failed:', err);
    }
    setBatchProcessing(false);
  };

  // Batch export / download
  const batchExport = async () => {
    if (selectedIds.size === 0) return;
    setBatchProcessing(true);
    try {
      const res = await fetch(`${API}/assets/batch/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asset_ids: [...selectedIds] }),
      });
      const data = await res.json();
      // Download each file
      for (const asset of (data.assets || [])) {
        const link = document.createElement('a');
        link.href = `${process.env.REACT_APP_BACKEND_URL}${asset.url}`;
        link.download = asset.original_name || asset.stored_name;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        await new Promise(r => setTimeout(r, 300)); // stagger downloads
      }
    } catch (err) {
      console.error('Export failed:', err);
    }
    setBatchProcessing(false);
  };

  const handleUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const fileList = Array.from(files);
    setUploadProgress({ current: 0, total: fileList.length, files: [] });
    const chunkSize = 10;
    const allResults = [];
    for (let i = 0; i < fileList.length; i += chunkSize) {
      const chunk = fileList.slice(i, i + chunkSize);
      const formData = new FormData();
      chunk.forEach(f => formData.append('files', f));
      try {
        const res = await fetch(`${API}/assets/upload`, { method: 'POST', body: formData });
        const data = await res.json();
        allResults.push(...(data.results || []));
        setUploadProgress(prev => ({ ...prev, current: Math.min(i + chunkSize, fileList.length), files: allResults }));
      } catch (err) {
        console.error('Upload chunk failed:', err);
        chunk.forEach(f => allResults.push({ filename: f.name, status: 'error', reason: err.message }));
      }
    }
    setUploadProgress(prev => ({ ...prev, current: fileList.length, files: allResults }));
    setUploading(false);
    fetchAssets();
  }, [fetchAssets]);

  const handleDrop = useCallback((e) => { e.preventDefault(); setIsDragging(false); handleUpload(e.dataTransfer.files); }, [handleUpload]);
  const handleDragOver = useCallback((e) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); setIsDragging(false); }, []);

  const deleteAsset = async (assetId) => {
    try {
      await fetch(`${API}/assets/${assetId}`, { method: 'DELETE' });
      setAssets(prev => prev.filter(a => a.asset_id !== assetId));
      setTotalAssets(prev => prev - 1);
      if (selectedAsset?.asset_id === assetId) setSelectedAsset(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const updateCategory = async (assetId, catId) => {
    // Multi-category toggle: add/remove from categories array
    const asset = assets.find(a => a.asset_id === assetId);
    const current = asset?.categories || (asset?.category && asset.category !== 'uncategorized' ? [asset.category] : []);
    let updated;
    if (current.includes(catId)) {
      updated = current.filter(c => c !== catId);
    } else {
      updated = [...current, catId];
    }
    const primary = updated.length > 0 ? updated[0] : 'uncategorized';
    try {
      await fetch(`${API}/assets/${assetId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: primary, categories: updated }),
      });
      setAssets(prev => prev.map(a => a.asset_id === assetId ? { ...a, category: primary, categories: updated } : a));
      if (selectedAsset?.asset_id === assetId) setSelectedAsset(prev => ({ ...prev, category: primary, categories: updated }));
    } catch (err) {
      console.error('Update failed:', err);
    }
  };

  const filteredAssets = searchQuery
    ? assets.filter(a => a.original_name.toLowerCase().includes(searchQuery.toLowerCase()))
    : assets;

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <div className="min-h-screen bg-texture" data-testid="asset-manager-page">
      {/* Header */}
      <div className="border-b" style={{ borderColor: 'rgba(0,240,255,0.08)', background: 'rgba(0,240,255,0.01)' }}>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-1.5 h-6 rounded-full" style={{ background: '#00F0FF' }} />
                <h1 className="text-xl font-black" style={{ fontFamily: 'Orbitron', color: '#00F0FF' }}>ASSET MANAGER</h1>
              </div>
              <p className="text-xs text-gray-500 ml-4">Bulk upload, organize, and manage all product photos and brand assets</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right mr-4">
                <p className="text-2xl font-black" style={{ color: '#D4AF37' }}>{totalAssets}</p>
                <p className="text-[9px] text-gray-500" style={{ fontFamily: 'Orbitron' }}>TOTAL ASSETS</p>
              </div>
              <button
                data-testid="select-mode-btn"
                onClick={() => selectMode ? exitSelectMode() : setSelectMode(true)}
                className={`px-4 py-2.5 rounded-full text-xs font-black flex items-center gap-2 transition-all ${
                  selectMode ? 'text-black' : 'text-gray-300 hover:text-white'
                }`}
                style={selectMode
                  ? { background: '#D4AF37', boxShadow: '0 0 15px rgba(212,175,55,0.3)' }
                  : { background: 'rgba(30,30,30,0.6)', border: '1px solid rgba(255,255,255,0.08)' }
                }
              >
                <Layers size={14} />
                {selectMode ? 'EXIT SELECT' : 'SELECT'}
              </button>
              <button
                data-testid="upload-trigger-btn"
                onClick={() => fileInputRef.current?.click()}
                className="px-5 py-2.5 rounded-full text-xs font-black flex items-center gap-2 transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #00F0FF, #0088AA)', color: '#000', boxShadow: '0 0 20px rgba(0,240,255,0.2)' }}>
                <Upload size={14} />
                UPLOAD FILES
              </button>
              <input ref={fileInputRef} type="file" multiple accept="image/*" className="hidden"
                onChange={(e) => handleUpload(e.target.files)} data-testid="file-input" />
            </div>
          </div>
        </div>
      </div>

      {/* Batch Action Bar — slides in when items selected */}
      {selectMode && selectedIds.size > 0 && (
        <div className="sticky top-0 z-[100] border-b" data-testid="batch-action-bar"
          style={{ background: 'rgba(10,10,15,0.97)', backdropFilter: 'blur(12px)', borderColor: 'rgba(212,175,55,0.2)' }}>
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-black" style={{ color: '#D4AF37' }}>{selectedIds.size}</span>
              <span className="text-xs text-gray-400">selected</span>
              <button onClick={selectAll} className="text-[10px] text-cyan-400 hover:text-cyan-300 underline" data-testid="select-all-btn">
                {selectedIds.size === filteredAssets.length ? 'Deselect All' : 'Select All on Page'}
              </button>
            </div>
            <div className="flex items-center gap-2">
              {/* Batch Category */}
              <div className="relative">
                <button
                  data-testid="batch-category-btn"
                  onClick={() => setBatchAction(batchAction === 'category' ? null : 'category')}
                  disabled={batchProcessing}
                  className="px-3 py-2 rounded-lg text-[10px] font-bold flex items-center gap-1.5 transition-all hover:bg-[rgba(0,240,255,0.1)]"
                  style={{ border: '1px solid rgba(0,240,255,0.2)', color: '#00F0FF' }}
                >
                  <Tag size={12} /> Set Category
                </button>
                {batchAction === 'category' && (
                  <div className="absolute top-full right-0 mt-1 w-52 rounded-xl overflow-hidden z-50 shadow-2xl"
                    style={{ background: '#111', border: '1px solid rgba(0,240,255,0.15)' }}>
                    <div className="p-2 border-b flex items-center justify-between" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                      <span className="text-[9px] text-gray-400 font-bold" style={{ fontFamily: 'Orbitron' }}>ASSIGN CATEGORY</span>
                      <button onClick={() => setBatchAction(null)}><X size={10} className="text-gray-600" /></button>
                    </div>
                    <div className="max-h-64 overflow-y-auto p-1">
                      {CATEGORIES.filter(c => c.id !== 'all').map(c => (
                        <button
                          key={c.id}
                          onClick={() => batchSetCategory(c.id)}
                          data-testid={`batch-cat-${c.id}`}
                          className="w-full text-left px-3 py-2 text-[10px] font-bold rounded-lg transition-all hover:bg-[rgba(0,240,255,0.08)] text-gray-300 hover:text-white"
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Batch Export */}
              <button
                data-testid="batch-export-btn"
                onClick={batchExport}
                disabled={batchProcessing}
                className="px-3 py-2 rounded-lg text-[10px] font-bold flex items-center gap-1.5 transition-all hover:bg-[rgba(34,197,94,0.1)]"
                style={{ border: '1px solid rgba(34,197,94,0.2)', color: '#22C55E' }}
              >
                <Download size={12} /> Export
              </button>

              {/* Batch Delete */}
              <button
                data-testid="batch-delete-btn"
                onClick={batchDelete}
                disabled={batchProcessing}
                className="px-3 py-2 rounded-lg text-[10px] font-bold flex items-center gap-1.5 transition-all hover:bg-[rgba(220,38,38,0.1)]"
                style={{ border: '1px solid rgba(220,38,38,0.2)', color: '#DC2626' }}
              >
                <Trash2 size={12} /> Delete
              </button>

              {/* Cancel */}
              <button onClick={exitSelectMode}
                className="px-3 py-2 rounded-lg text-[10px] font-bold text-gray-500 hover:text-gray-300 transition-all">
                <XCircle size={14} />
              </button>
            </div>
          </div>
          {batchProcessing && (
            <div className="h-0.5 w-full overflow-hidden">
              <div className="h-full animate-pulse" style={{ background: 'linear-gradient(90deg, #00F0FF, #D4AF37, #00F0FF)', width: '100%' }} />
            </div>
          )}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Drop Zone */}
        <div
          data-testid="drop-zone"
          onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={`rounded-2xl border-2 border-dashed mb-6 transition-all cursor-pointer ${isDragging ? 'scale-[1.01]' : ''}`}
          style={{
            borderColor: isDragging ? '#00F0FF' : 'rgba(0,240,255,0.15)',
            background: isDragging ? 'rgba(0,240,255,0.05)' : 'rgba(0,240,255,0.01)',
            minHeight: uploading ? '180px' : '140px',
          }}
        >
          {uploading ? (
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Loader size={16} className="text-[#00F0FF] animate-spin" />
                  <span className="text-sm font-bold" style={{ color: '#00F0FF' }}>
                    Uploading... {uploadProgress.current} / {uploadProgress.total}
                  </span>
                </div>
                <span className="text-xs text-gray-500">{Math.round((uploadProgress.current / uploadProgress.total) * 100)}%</span>
              </div>
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'rgba(0,240,255,0.1)' }}>
                <div className="h-full rounded-full transition-all duration-300"
                  style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%`, background: 'linear-gradient(90deg, #00F0FF, #0088AA)', boxShadow: '0 0 10px rgba(0,240,255,0.5)' }} />
              </div>
              {uploadProgress.files.length > 0 && (
                <div className="mt-3 max-h-20 overflow-y-auto space-y-1">
                  {uploadProgress.files.slice(-5).map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-[10px]">
                      {f.status === 'uploaded' ? <Check size={10} className="text-green-400" /> : <AlertCircle size={10} className="text-red-400" />}
                      <span className={f.status === 'uploaded' ? 'text-gray-400' : 'text-red-400'}>{f.filename}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 flex flex-col items-center justify-center gap-3">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ background: 'rgba(0,240,255,0.06)', border: '1px solid rgba(0,240,255,0.12)' }}>
                <Upload size={24} className="text-[#00F0FF]" />
              </div>
              <div className="text-center">
                <p className="text-sm font-bold text-white">Drop files here or click to upload</p>
                <p className="text-xs text-gray-500 mt-1">PNG, JPG, WEBP, GIF, SVG — No limit on file count</p>
              </div>
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
          <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
            {CATEGORIES.map(c => (
              <button
                key={c.id}
                onClick={() => { setCategory(c.id); setPage(1); }}
                data-testid={`cat-${c.id}`}
                className={`px-3 py-1.5 rounded-full text-[10px] font-bold whitespace-nowrap transition-all ${
                  category === c.id ? 'text-black' : 'text-gray-500 hover:text-gray-300'
                }`}
                style={category === c.id
                  ? { background: '#00F0FF', boxShadow: '0 0 10px rgba(0,240,255,0.3)' }
                  : { background: 'rgba(30,30,30,0.5)', border: '1px solid rgba(255,255,255,0.06)' }
                }
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-600" />
              <input data-testid="asset-search" type="text" placeholder="Search files..."
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-7 pr-3 py-1.5 rounded-lg text-[10px] bg-[rgba(20,20,20,0.8)] border text-white placeholder-gray-600 focus:border-[#00F0FF] focus:outline-none w-40"
                style={{ borderColor: 'rgba(255,255,255,0.06)' }} />
            </div>
            <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
              <button onClick={() => setViewMode('grid')}
                className={`p-1.5 ${viewMode === 'grid' ? 'bg-[rgba(0,240,255,0.15)]' : 'bg-[rgba(20,20,20,0.5)]'}`}>
                <Grid size={12} className={viewMode === 'grid' ? 'text-[#00F0FF]' : 'text-gray-600'} />
              </button>
              <button onClick={() => setViewMode('list')}
                className={`p-1.5 ${viewMode === 'list' ? 'bg-[rgba(0,240,255,0.15)]' : 'bg-[rgba(20,20,20,0.5)]'}`}>
                <List size={12} className={viewMode === 'list' ? 'text-[#00F0FF]' : 'text-gray-600'} />
              </button>
            </div>
          </div>
        </div>

        {/* Asset Grid */}
        {filteredAssets.length === 0 ? (
          <div className="text-center py-20">
            <Image size={48} className="text-gray-800 mx-auto mb-4" />
            <p className="text-sm text-gray-500">No assets yet. Drop some files above to get started.</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2" data-testid="asset-grid">
            {filteredAssets.map(asset => {
              const isSelected = selectedIds.has(asset.asset_id);
              return (
                <div
                  key={asset.asset_id}
                  className={`group relative rounded-xl overflow-hidden cursor-pointer transition-all hover:ring-1 ${
                    isSelected ? 'ring-2 ring-[#D4AF37]' : selectedAsset?.asset_id === asset.asset_id ? 'ring-2 ring-[#00F0FF]' : ''
                  }`}
                  style={{ background: '#0A0A0A' }}
                  onClick={() => selectMode ? toggleSelect(asset.asset_id) : setSelectedAsset(asset)}
                  data-testid={`asset-${asset.asset_id}`}
                >
                  <div className="aspect-square">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${asset.url}`} alt={asset.original_name}
                      className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  {/* Select mode checkbox */}
                  {selectMode && (
                    <div className={`absolute top-1.5 left-1.5 w-5 h-5 rounded flex items-center justify-center transition-all ${
                      isSelected ? 'bg-[#D4AF37]' : 'bg-black/60 border border-white/20'
                    }`}>
                      {isSelected ? <Check size={10} className="text-black" /> : null}
                    </div>
                  )}
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-1.5">
                    <p className="text-[8px] text-white truncate w-full">{asset.original_name}</p>
                  </div>
                  {/* Category badges */}
                  {(() => {
                    const cats = asset.categories || (asset.category && asset.category !== 'uncategorized' ? [asset.category] : []);
                    return cats.length > 0 && (
                      <div className="absolute top-1 right-1 flex flex-wrap gap-0.5 max-w-[80%] justify-end">
                        {cats.slice(0, 2).map(c => (
                          <span key={c} className="px-1 py-0.5 rounded-full text-[6px] text-[#00F0FF]"
                            style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>{c}</span>
                        ))}
                        {cats.length > 2 && (
                          <span className="px-1 py-0.5 rounded-full text-[6px] text-[#D4AF37]"
                            style={{ background: 'rgba(0,0,0,0.7)' }}>+{cats.length - 2}</span>
                        )}
                      </div>
                    );
                  })()}
                  {/* Delete button (only in non-select mode) */}
                  {!selectMode && (
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteAsset(asset.asset_id); }}
                      className="absolute bottom-1 right-1 w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ background: 'rgba(220,38,38,0.8)' }}
                      data-testid={`delete-${asset.asset_id}`}
                    >
                      <X size={8} className="text-white" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-1" data-testid="asset-list">
            {filteredAssets.map(asset => {
              const isSelected = selectedIds.has(asset.asset_id);
              return (
                <div
                  key={asset.asset_id}
                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all hover:bg-[rgba(0,240,255,0.03)] ${
                    isSelected ? 'bg-[rgba(212,175,55,0.08)] border-[rgba(212,175,55,0.2)]' : selectedAsset?.asset_id === asset.asset_id ? 'bg-[rgba(0,240,255,0.05)]' : ''
                  }`}
                  style={{ border: `1px solid ${isSelected ? 'rgba(212,175,55,0.2)' : 'rgba(255,255,255,0.03)'}` }}
                  onClick={() => selectMode ? toggleSelect(asset.asset_id) : setSelectedAsset(asset)}
                >
                  {selectMode && (
                    <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'bg-[#D4AF37]' : 'bg-[rgba(30,30,30,0.5)] border border-white/10'
                    }`}>
                      {isSelected && <Check size={10} className="text-black" />}
                    </div>
                  )}
                  <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0" style={{ background: '#0A0A0A' }}>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${asset.url}`} alt={asset.original_name}
                      className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold truncate">{asset.original_name}</p>
                    <p className="text-[9px] text-gray-600">{formatSize(asset.size)} • {asset.category}</p>
                  </div>
                  {!selectMode && (
                    <button onClick={(e) => { e.stopPropagation(); deleteAsset(asset.asset_id); }}
                      className="p-1.5 rounded-lg hover:bg-[rgba(255,0,0,0.1)] transition-colors">
                      <Trash2 size={12} className="text-gray-600 hover:text-red-400" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-2 rounded-lg disabled:opacity-20 hover:bg-[rgba(0,240,255,0.05)]"
              style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
              <ChevronLeft size={14} className="text-gray-400" />
            </button>
            <span className="text-xs text-gray-500">Page {page} of {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
              className="p-2 rounded-lg disabled:opacity-20 hover:bg-[rgba(0,240,255,0.05)]"
              style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
              <ChevronRight size={14} className="text-gray-400" />
            </button>
          </div>
        )}
      </div>

      {/* Asset Detail Sidebar (only in non-select mode) */}
      {selectedAsset && !selectMode && (
        <div className="fixed top-0 right-0 bottom-0 w-80 z-[9998] flex flex-col"
          style={{ background: 'rgba(13,13,13,0.98)', backdropFilter: 'blur(20px)', borderLeft: '1px solid rgba(0,240,255,0.1)', animation: 'slideInRight 0.2s ease-out' }}
          data-testid="asset-detail-panel">
          <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'rgba(0,240,255,0.08)' }}>
            <span className="text-xs font-black" style={{ fontFamily: 'Orbitron', color: '#00F0FF' }}>ASSET DETAIL</span>
            <button onClick={() => setSelectedAsset(null)}
              className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-[rgba(255,255,255,0.05)]">
              <X size={14} className="text-gray-400" />
            </button>
          </div>
          <div className="p-4 overflow-y-auto flex-1">
            <div className="rounded-xl overflow-hidden mb-4" style={{ background: '#0A0A0A' }}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${selectedAsset.url}`} alt={selectedAsset.original_name}
                className="w-full object-contain max-h-64" />
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[9px] text-gray-600 block mb-0.5" style={{ fontFamily: 'Orbitron' }}>FILENAME</label>
                <p className="text-xs font-bold truncate">{selectedAsset.original_name}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[9px] text-gray-600 block mb-0.5" style={{ fontFamily: 'Orbitron' }}>SIZE</label>
                  <p className="text-xs text-gray-300">{formatSize(selectedAsset.size)}</p>
                </div>
                <div>
                  <label className="text-[9px] text-gray-600 block mb-0.5" style={{ fontFamily: 'Orbitron' }}>TYPE</label>
                  <p className="text-xs text-gray-300">{selectedAsset.type}</p>
                </div>
              </div>
              <div>
                <label className="text-[9px] text-gray-600 block mb-1.5" style={{ fontFamily: 'Orbitron' }}>CATEGORIES <span className="text-gray-700">(multi-select)</span></label>
                <div className="flex flex-wrap gap-1">
                  {CATEGORIES.filter(c => c.id !== 'all' && c.id !== 'uncategorized').map(c => {
                    const cats = selectedAsset.categories || (selectedAsset.category && selectedAsset.category !== 'uncategorized' ? [selectedAsset.category] : []);
                    const isActive = cats.includes(c.id);
                    return (
                      <button key={c.id} onClick={() => updateCategory(selectedAsset.asset_id, c.id)}
                        className={`px-2 py-1 rounded-full text-[9px] font-bold transition-all ${
                          isActive ? 'text-black' : 'text-gray-500 hover:text-gray-300'
                        }`}
                        style={isActive
                          ? { background: '#00F0FF' }
                          : { background: 'rgba(30,30,30,0.5)', border: '1px solid rgba(255,255,255,0.06)' }
                        }>
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-[9px] text-gray-600 block mb-1" style={{ fontFamily: 'Orbitron' }}>ASSET URL</label>
                <input readOnly value={`${process.env.REACT_APP_BACKEND_URL}${selectedAsset.url}`}
                  className="w-full px-2 py-1.5 rounded-lg text-[9px] bg-[rgba(20,20,20,0.8)] border text-gray-400 focus:outline-none"
                  style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                  onClick={(e) => { e.target.select(); navigator.clipboard.writeText(e.target.value); }}
                  data-testid="asset-url-input" />
                <p className="text-[8px] text-gray-600 mt-0.5">Click to copy</p>
              </div>
              <div>
                <label className="text-[9px] text-gray-600 block mb-0.5" style={{ fontFamily: 'Orbitron' }}>UPLOADED</label>
                <p className="text-xs text-gray-300">
                  {new Date(selectedAsset.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          </div>
          <div className="p-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
            <button onClick={() => deleteAsset(selectedAsset.asset_id)} data-testid="delete-selected-btn"
              className="w-full py-2.5 rounded-full text-xs font-bold flex items-center justify-center gap-2 transition-all hover:bg-[rgba(220,38,38,0.15)]"
              style={{ border: '1px solid rgba(220,38,38,0.2)', color: '#DC2626' }}>
              <Trash2 size={12} /> Delete Asset
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
